import React, { useState, useEffect } from 'react';
import Button from '../../../Components/Button/Button';
import { getApi, postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import SubscriptionPlans from './SubscriptionPlans';
import {
  TRIAL_DAYS,
  formatApiDate,
  formatTrialLabel,
  getTrialDaysLeft,
  isTrialingStatus,
  isLiveSubscriptionStatus,
} from '../../../Utils/subscriptionTrial';

// The subscription API only returns plan name/price, so the feature copy lives
// here, keyed by plan name. Property limits match PLAN_TIER_DETAILS in SubscriptionPlans.
const COMMON_PLAN_FEATURES = [
  'Full tenant screening reports',
  'Automated verification results',
  'Reference check summaries',
  'Document authenticity analysis',
];

const PLAN_FEATURES = {
  basic: ['Up to 5 property listings', ...COMMON_PLAN_FEATURES],
  standard: ['Up to 15 property listings', ...COMMON_PLAN_FEATURES],
  enterprise: ['Unlimited property listings', ...COMMON_PLAN_FEATURES],
};

const PLAN_DESCRIPTIONS = {
  basic: 'Screening features for landlords getting started',
  standard: 'Screening features for growing portfolios',
  enterprise: 'Full access to all screening features',
};

const getPlanFeatures = (planName) =>
  PLAN_FEATURES[String(planName || '').trim().toLowerCase()] || COMMON_PLAN_FEATURES;

const getPlanDescription = (planName) =>
  PLAN_DESCRIPTIONS[String(planName || '').trim().toLowerCase()] || 'Full access to all screening features';

const SubscriptionDetail = () => {
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [payoutAccountData, setPayoutAccountData] = useState(null);
  const [payoutLoader, setPayoutLoader] = useState(false);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [connectorAccountData, setConnectorAccountData] = useState('');
  const [cancelPlanModalOpen, setCancelPlanModalOpen] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
    fetchConnectedAccountStatus();
  }, []);

  const fetchConnectedAccountStatus = async () => {
    try {
      const response = await getApi('/landlord/check-connected-account');
      if (response?.statusCode === 200 && response?.data) {
        setPayoutAccountData(response.data);
      }
    } catch (error) {
      console.error('Error fetching connected account status:', error);
    }
  };

  const fetchSubscriptionData = async () => {
    setLoading(true);
    try {
      // Fetch subscription details from API
      const subscriptionRes = await getApi('/subscriptions/details');
      
      // Handle different possible response structures
      const subscriptionData = 
        subscriptionRes?.data ||
        subscriptionRes;

      if (subscriptionData) {
        // The API returns a flat structure with subscription details
        setSubscription(subscriptionData);
        
        // Create plan object from subscription data
        const planData = {
          plan_name: subscriptionData.plan_name,
          amount_per_month: subscriptionData.amount_per_month,
          currency: subscriptionData.currency,
          interval: subscriptionData.interval,
        };
        
        setPlan(planData);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load subscription data';
      toast.error(errorMessage);
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCancelPlanModal = () => {
    if (!subscription || !isActive) {
      toast.error('No active subscription found');
      return;
    }
    setCancelPlanModalOpen(true);
  };

  const closeCancelPlanModal = () => {
    setCancelPlanModalOpen(false);
  };

  const confirmCancelPlan = async () => {
    setCancelPlanModalOpen(false);
    setCancelling(true);
    try {
      const response = await postApi('/subscriptions/cancel');
      
      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Subscription cancelled successfully');
        
        // Update subscription status in localStorage
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          if (userData?.user_details?.UserSubscription) {
            userData.user_details.UserSubscription.status = 'cancelled';
            localStorage.setItem('userData', JSON.stringify(userData));
          }
        }
        
        // Refresh subscription data
        await fetchSubscriptionData();
      } else {
        toast.error(response?.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to cancel subscription';
      toast.error(errorMessage);
      console.error('Error cancelling subscription:', error);
    } finally {
      setCancelling(false);
    }
  };

  const getStripeConnectUrl = (data) => {
    if (!data) return null;

    const isValidStripeUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      if (!url.startsWith('http')) return false;
      try {
        const parsed = new URL(url);
        return parsed.hostname.endsWith('stripe.com');
      } catch {
        return false;
      }
    };

    if (typeof data === 'string' && isValidStripeUrl(data)) {
      return data;
    }

    const possibleUrls = [
      data.onboardingUrl,
      data.onboarding_url,
      data.url,
      data.accountLink,
      data.account_link,
      data.redirectUrl,
      data.redirect_url,
    ];

    for (const value of possibleUrls) {
      if (isValidStripeUrl(value)) {
        return value;
      }
    }

    return null;
  };

  const handleAddPayoutMethod = async () => {
    setPayoutLoader(true);
    try {
      const response = await postApi('/subscriptions/connected-account', {});
      if (response?.statusCode === 200) {
        const stripeUrl = getStripeConnectUrl(response?.data);
        if (stripeUrl) {
          setConnectorAccountData(stripeUrl);
          setPayoutModalOpen(true);
        } else {
          toast.success(response?.message || 'Stripe account connected successfully');
          fetchConnectedAccountStatus();
        }
      } else {
        toast.error(response?.message || 'Failed to create payout setup');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to add payout method';
      toast.error(errorMessage);
    } finally {
      setPayoutLoader(false);
    }
  };

  const handlePayoutVerify = () => {
    if (connectorAccountData) {
      window.location.href = connectorAccountData;
    }
  };

  // Timestamps come back as millisecond strings ("1771481871000") for billing
  // dates and as Stripe seconds for trial_end - formatApiDate reads both.
  const formatDate = (dateString) => formatApiDate(dateString);

  // Format price helper
  const formatPrice = (price) => {
    if (!price && price !== 0) return '$11.99';
    return `$${Number(price).toFixed(2)}`;
  };

  // Format card number helper (always masked)
  const formatCardNumber = (card) => {
    if (!card) return '';

    // Common shapes:
    // - { last4: "4242" }
    // - { last4: 4242 }
    // - { card_last4: "4242" }
    // - "4242"
    const last4Raw =
      card?.last4 ??
      card?.last_4 ??
      card?.card_last4 ??
      card?.cardLast4 ??
      card;

    const last4 = String(last4Raw || '').replace(/\D/g, '').slice(-4);
    if (!last4) return '';
    return `•••• ${last4}`;
  };

  // Get subscription status - a trial counts as a live subscription
  const subscriptionStatus = subscription?.status || 'inactive';
  const isTrialing = isTrialingStatus(subscriptionStatus);
  const isActive = isLiveSubscriptionStatus(subscriptionStatus);

  // Get next billing date (timestamp string from API)
  const nextBillingDate = subscription?.next_billing_date || null;

  // Trial countdown for the badge ("Trial - 9 days left"). Not every response
  // carries trial_end - while trialing the first charge lands the day the trial
  // ends, so next_billing_date is the same date.
  const trialEnd = subscription?.trial_end || subscription?.trialEnd || nextBillingDate || null;
  const trialDaysLeft = isTrialing ? getTrialDaysLeft(trialEnd) : null;

  // Get plan name
  const planName = subscription?.plan_name || plan?.plan_name || 'Landlord Pro Plan';
  const planFeatures = getPlanFeatures(planName);
  
  // Get plan price
  const planPrice = subscription?.amount_per_month || plan?.amount_per_month || 11.99;
  
  // Get billing interval (convert "monthly" to "month")
  const interval = subscription?.interval || plan?.interval || 'monthly';
  const priceUnit = interval === 'monthly' ? 'month' : interval;

  const maskedCardNumber = formatCardNumber(subscription?.card);

  const cancelAtPeriodEnd = subscription?.cancel_at_period_end === true || subscription?.cancel_at_period_end === 'true';

  if (loading) {
    return (
      <div className="subscription-detail-page">
        <div className="subscription-detail-container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-detail-page">
      <div className="subscription-detail-container">
        {/* Landlord Pro Plan Card */}
        <div className="subscription-plan-card">
          <div className="subscription-plan-header">
            <div className="subscription-plan-title-section">
              <div className="subscription-plan-badge-wrapper">
                <img 
                  src={window.getPublicUrl ? window.getPublicUrl("/assets/images/star_unfilled.png") : "/assets/images/star_unfilled.png"} 
                  alt="Star" 
                  className="subscription-plan-star-icon"
                />
                <span className={`subscription-plan-badge${isTrialing ? ' subscription-plan-badge-trial' : ''}`}>
                  {isTrialing
                    ? formatTrialLabel(trialEnd)
                    : isActive
                      ? 'Active'
                      : subscriptionStatus.charAt(0).toUpperCase() + subscriptionStatus.slice(1)}
                </span>
              </div>
              <h2 className="subscription-plan-title">{planName}</h2>
            </div>
            <div className="subscription-plan-price">
              <span className="subscription-price-amount">{formatPrice(planPrice)}</span>
              <span className="subscription-price-unit">/{priceUnit}</span>
            </div>
          </div>
          
          <p className="subscription-plan-description">
            {subscription?.description || plan?.description || getPlanDescription(planName)}
          </p>
          
          {isTrialing && (
            <div className={`subscription-trial-banner${trialDaysLeft === 0 ? ' subscription-trial-banner-ended' : ''}`}>
              <span className="subscription-trial-banner-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="subscription-trial-banner-body">
                <div className="subscription-trial-banner-title">
                  {trialDaysLeft === 0
                    ? `Your ${TRIAL_DAYS}-day free trial has ended`
                    : `Free trial - ${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'} left`}
                </div>
                <div className="subscription-trial-banner-text">
                  {trialDaysLeft === 0
                    ? `${formatPrice(planPrice)}/${priceUnit} billing has started.`
                    : trialEnd
                      ? `Your trial runs until ${formatDate(trialEnd)}. `
                        + `${formatPrice(planPrice)}/${priceUnit} will be charged after that unless you cancel.`
                      : `${formatPrice(planPrice)}/${priceUnit} will be charged when the trial ends unless you cancel.`}
                  {!maskedCardNumber && trialDaysLeft !== 0 && ' Add a card before it ends to keep your subscription.'}
                </div>
              </div>
            </div>
          )}

          <div className="subscription-billing-date">
            <img 
              src={window.getPublicUrl ? window.getPublicUrl("/assets/images/calendar-01.png") : "/assets/images/calendar-01.png"} 
              alt="Calendar" 
              className="subscription-calendar-icon"
            />
            <span>
              {isTrialing ? 'First billing date: ' : 'Next billing date: '}
              {formatDate(nextBillingDate)}
            </span>
          </div>

          {maskedCardNumber && (
            <div className="subscription-card-number" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Card ending in: {maskedCardNumber}</span>
            </div>
          )}

          <div className="subscription-plan-features">
            <h3 className="subscription-features-title">Plan includes:</h3>
            <ul className="subscription-features-list">
              {planFeatures.map((feature, index) => (
                <li key={index} className="subscription-feature-item">
                  <img 
                    src={window.getPublicUrl ? window.getPublicUrl("/assets/images/CircleCheckIcon.svg") : "/assets/images/CircleCheckIcon.svg"} 
                    alt="check" 
                    className="subscription-check-icon"
                  />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="subscription-plan-actions">
            {cancelAtPeriodEnd ? (
              <div className="subscription-cancelled-block">
                <span className="subscription-cancelled-label">Cancelled</span>
                <p className="subscription-cancelled-message">
                  Your current plan will be cancelled once the current billing period ends.
                </p>
              </div>
            ) : (
              <button 
                className="subscription-cancel-button"
                onClick={openCancelPlanModal}
                disabled={cancelling || !isActive}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Plan'}
              </button>
            )}
          </div>
        </div>

        {/* Available plans - activated plan is badged, the rest can be switched to */}
        <SubscriptionPlans
          currentPlanName={planName}
          onPlanChanged={fetchSubscriptionData}
        />

        {/* Payout method Section */}
        <div className="subscription-payment-method-card">
          <div className="subscription-payment-header">
            <div className="subscription-payment-info">
              <h3 className="subscription-payment-title">Payout method</h3>
              <p className="subscription-payment-subtitle">
                {payoutAccountData?.is_connected === 1
                  ? 'Your Stripe account is connected. Click "Connected" to view and manage your payouts on Stripe.'
                  : 'Add a payment method to get paid'}
              </p>
            </div>
            <div className="subscription-payment-actions">
              {payoutAccountData?.is_connected === 1 ? (
                <div className="subscription-payout-connected-block">
                  <img
                    src={window.getPublicUrl ? window.getPublicUrl("/assets/images/Stripe.svg") : "/assets/images/Stripe.svg"}
                    alt="Stripe"
                    className="subscription-payout-stripe-logo"
                  />
                  <button
                    type="button"
                    className="subscription-payout-connected"
                    title="Open your Stripe dashboard"
                    onClick={() => {
                      if (payoutAccountData?.manage_account_url) {
                        window.open(payoutAccountData.manage_account_url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    Connected
                  </button>
                </div>
              ) : (
                <Button
                  className="subscription-add-payout-button"
                  onClick={handleAddPayoutMethod}
                  disabled={payoutLoader}
                >
                  {payoutLoader && (
                    <span className="subscription-payout-spinner" role="status" aria-hidden="true" />
                  )}
                  Add Payout Method
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payout verification modal */}
      <Modal
        isOpen={payoutModalOpen}
        onRequestClose={() => setPayoutModalOpen(false)}
        contentLabel="Payout setup"
        overlayClassName="subscription-payout-modal-overlay"
        className="subscription-payout-modal"
        portalClassName="subscription-payout-modal-portal"
      >
        <button
          type="button"
          className="subscription-payout-modal-close"
          onClick={() => setPayoutModalOpen(false)}
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <p className="subscription-payout-modal-text">
          To complete your account setup, please verify your details. Once verified, your payout method will be successfully created, and you can start receiving payments.
        </p>
        <div className="subscription-payout-modal-buttons">
          <button type="button" className="subscription-payout-modal-cancel" onClick={() => setPayoutModalOpen(false)}>
            Cancel
          </button>
          <button type="button" className="subscription-payout-modal-verify" onClick={handlePayoutVerify}>
            Verify
          </button>
        </div>
      </Modal>

      {/* Cancel Plan confirmation modal */}
      <Modal
        isOpen={cancelPlanModalOpen}
        onRequestClose={closeCancelPlanModal}
        contentLabel="Cancel subscription"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="logout-modal"
        overlayClassName="logout-modal-overlay"
      >
        <button className="logout-modal-close" onClick={closeCancelPlanModal} type="button" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h2 className="logout-modal-title">Cancel subscription</h2>
        <p className="logout-modal-message">Are you sure you want to cancel your subscription?</p>
        <div className="logout-modal-buttons">
          <button type="button" className="logout-modal-cancel" onClick={closeCancelPlanModal}>Cancel</button>
          <button type="button" className="logout-modal-confirm" onClick={confirmCancelPlan}>OK</button>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionDetail;
